"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShoppingBag, Store, Search, ShieldCheck, Layers, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const retailFocusAreas = [{
  title: "DTC & Brand Stores",
  description: "Structuring high-converting collection hubs, category taxonomy, and product schema.",
  icon: ShoppingBag
}, {
  title: "Multi-Brand Marketplaces",
  description: "Resolving faceted navigation, crawl bloat, and product indexation at scale.",
  icon: Store
}, {
  title: "Modern Search Visibility",
  description: "Aligning product catalogs with both traditional search algorithms and generative AI engines.",
  icon: Search
}];
const directPillars = ["Direct consultant access — you work directly with me on every audit", "Tailored search strategies built for your product margins", "Clear technical fixes without agency account managers"];
export default function IndustryHero() {
  return <section id="industryhubhero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 lg:py-28">
      {/* Subtle background decorative accents matching cobalt & blush palette */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-secondary/60 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -left-24 size-80 rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Main Content Column */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            
            {/* Direct Personal Brand Eyebrow */}
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 shadow-xs mb-6">
              <span className="size-2 rounded-full bg-accent animate-pulse" />
              <span className="text-xs font-medium text-foreground tracking-wide">
                Direct Freelance SEO Consultancy
              </span>
              <span className="text-xs text-muted-foreground">|</span>
              <span className="text-xs font-semibold text-primary">
                Direct consultant access — you work directly with me
              </span>
            </div>

            {/* H1 Heading */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-serif font-bold text-foreground tracking-tight leading-[1.15] mb-6">
              SEO strategy for online stores, DTC brands, and marketplace sellers
            </h1>

            {/* Supporting Copy */}
            <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed max-w-2xl mb-8">
              I help ecommerce merchants, DTC brands, and retail marketplaces turn organic search into a reliable, high-converting revenue driver. From resolving faceted navigation bloat and indexation bottlenecks to structuring revenue-focused product taxonomy and rich schema, my strategies are engineered to grow organic sales and protect your margins.
            </p>

            {/* CTA Group */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="rounded-md font-medium shadow-sm group">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4 ml-2 transition-[transform] group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md font-medium border-border hover:bg-secondary/50">
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </div>

            {/* Direct Consultant Value Propositions */}
            <div className="pt-6 border-t border-border w-full">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
                How I partner with retail founders & heads of growth
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {directPillars.map((pillar, i) => <li key={pillar} data-index={i} className="flex items-start gap-2 text-sm text-foreground/90">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{pillar}</span>
                  </li>)}
              </ul>
            </div>

          </div>

          {/* Right Column: Visual Focus Card & Expert Architecture Panel */}
          <div className="lg:col-span-5 relative w-full">
            <div className="relative rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-lg">
              
              {/* Profile / Hub Context Header */}
              <div className="flex items-center justify-between pb-6 mb-6 border-b border-border">
                <div className="flex items-center gap-3.5">
                  <div className="relative size-12 rounded-full overflow-hidden border border-border bg-muted">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi, Freelance SEO Consultant" loading="lazy" decoding="async" className="size-full object-cover" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-foreground leading-tight">
                      Yassine Essaadi
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Independent SEO Consultant
                    </p>
                  </div>
                </div>

                <Badge variant="secondary" className="font-normal text-xs py-1 px-2.5 bg-secondary text-secondary-foreground">
                  Retail & DTC Hub
                </Badge>
              </div>

              {/* Focus Pillars */}
              <div className="space-y-4 mb-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Core eCommerce Search Focus
                </p>

                <div className="space-y-3">
                  {retailFocusAreas.map((area, i) => {
                  const Icon = area.icon;
                  return <div key={area.title} data-index={i} className="p-3.5 rounded-lg border border-border/80 bg-background hover:border-primary/40 transition-[border-color,background-color] duration-200">
                        <div className="flex items-start gap-3">
                          <div className="size-8 rounded-md bg-secondary/70 flex items-center justify-center shrink-0 text-primary mt-0.5">
                            <Icon className="size-4" />
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-foreground">
                              {area.title}
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                              {area.description}
                            </p>
                          </div>
                        </div>
                      </div>;
                })}
                </div>
              </div>

              {/* Bottom Guarantee / Positioning Note */}
              <div className="rounded-lg bg-secondary/40 border border-border/60 p-3.5 flex items-center gap-3">
                <ShieldCheck className="size-5 text-primary shrink-0" />
                <p className="text-xs text-muted-foreground leading-snug">
                  100% hands-on implementation and direct accountability. No outsourcing.
                </p>
              </div>

            </div>

            {/* Decorative Subtle Corner Stamp */}
            <div aria-hidden="true" className="hidden sm:flex absolute -bottom-4 -right-4 items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-primary-foreground text-xs font-medium shadow-md">
              <Sparkles className="size-3 text-accent" />
              <span>YE Consultancy</span>
            </div>

          </div>

        </div>
      </div>
    </section>;
}